import type { ApiError } from '@ctcells/api'
import type { PreflightResult, PreflightWarning, WarnAckReason } from '@ctcells/contract'

/**
 * 작업 줄 [시작] 앞에 서는 **게이트 모델** — 순수 함수뿐이다(그리기·요청 없음).
 *
 * 🔴 **프론트가 판정하지 않는다.** 여기 있는 것은 전부 「서버가 준 값을 어느 대화로 보낼지」
 *    한 갈래뿐이고, **무엇을 확약해야 하는지는 서버가 정한다**:
 *    - Pre-flight 가 준 `warnings[]` 를 **거르지 않고 전부** 보이고, 사람이 확인하면
 *      **그 코드 전부**를 싣는다(`ackCodes`). 화면이 고를 자리가 없다.
 *    - 서버가 `warn_ack_mismatch` 로 정답(`context.actual`)을 주면 **그것으로 갈아**
 *      다시 확약받는다(`startGateFromError`). 자동 재시도는 하지 않는다 — 사람이 다시 누른다.
 *
 * 🔴 **`blockers` 코드를 확약에 섞지 마라.** 서버 대조값은 `sorted({w.code for w in warnings})`
 *    하나다(`ctcells-core app/views/batches.py:706`). 섞으면 그 자리에서 `warn_ack_mismatch` 다.
 *
 * 🔴 **`warnings` 가 «없는 것»과 «빈 것»을 가른다.** `?? []` 로 접으면 「이 판이 경고를 안
 *    싣는다」가 「경고 0건」이 되고, 그 순간 확약 없이 시작이 나가 서버 409 로 떨어진다.
 *    ⚠ 다만 «없는 것»도 시작을 막지는 않는다 — 보내 보고 서버가 `warn_ack_required` 로
 *      거절하면 그 응답이 실은 `context.warnings` 로 목록을 가르쳐 준다(아래 참조).
 *
 * ⚠ **시작은 서버가 같은 트랜잭션에서 Pre-flight 를 다시 잰다**(`SM-BATCH-07`). 화면이 들고
 *   있던 목록이 그 사이 바뀔 수 있어서, 시작 직전에 한 번 더 재는 이 순서가 필요하다.
 */

/** 시작을 막아 세운 자리. `null` 이면 게이트가 없다 = 바로 시작한다 */
export type StartGate =
  | { phase: 'confirm'; result: PreflightResult }
  | {
      /** 차단 — 확약으로 우회되지 않는다. 시작 버튼 자체가 없다 */
      phase: 'blocked'
      /** 서버가 준 Pre-flight 결과 **그대로**. 그리는 것은 `PreflightReport` 가 한다 */
      result: PreflightResult
    }
  | {
      /** 경고 — 막지 않는 대신 확약을 요구한다 */
      phase: 'warn'
      warnings: PreflightWarning[]
      /**
       * 그 경고가 실린 Pre-flight 결과 **전체**. 있으면 대화가 보고서를 통째로 그린다 —
       * 🔴 **미판정 항목을 같이 보여 주기 위해서다.** 서버가 통과시킨 회차에도
       *    `verdict: 'inconclusive'` 가 남아 있을 수 있고(실측 2026-09-22 — 비전·시약 온도·
       *    소모품 4건), 그걸 숨기면 사람이 **「못 쟀다」를 모른 채** 확약한다.
       * ⚠ 서버가 정정해 준 목록(`reason` 있음)에는 **없다** — 그 결과는 이미 낡았다.
       */
      result?: PreflightResult | undefined
      /**
       * 이 목록이 **시작 거절과 함께 서버가 정정해 준 것**일 때 그 갈래.
       * 없으면 Pre-flight 응답에서 바로 온 첫 목록이다.
       */
      reason?: WarnAckReason | undefined
    }

/**
 * Pre-flight 응답 → 게이트. `null` 이면 게이트 없음(바로 시작).
 *
 * 🔴 **막는 축은 `passed` «하나»다 — `blockers.length` 가 아니다**(2026-09-22 실물에서
 *    걸렸다). `blockers` 는 「막혔다」와 **「못 쟀다」**를 같은 배열에 담는다:
 *    막는 것은 `verdict: 'fail'` 뿐이고 `inconclusive` 는 서버 설정
 *    (`preflight_skip_unevaluable`)에 따라 통과한다 — 그 판정이 `passed` 다
 *    (`ctcells-core app/services/preflight.py:751 _passed`).
 *    ⚠ 실측: `passed: true` · `blockers` 4건(전부 `inconclusive`) · 서버
 *      `allowed_commands` 에 `start` 있음. 길이로 재면 **서버가 열어 둔 시작을 화면이 막는다.**
 * 🔴 **같은 판정을 서버가 이미 `allowed_commands` 에 반영한다**(`Transition.requires_preflight`)
 *    — 그래서 `blocked` 얼굴은 실제로는 드문 경합(목록을 받은 뒤 상태가 바뀐 경우)이다.
 *    그래도 둔다: 그 순간 사람이 무엇 때문에 못 시작하는지 볼 자리가 있어야 한다.
 */
export function startGateFromPreflight(result: PreflightResult): StartGate | null {
  if (!result.passed) return { phase: 'blocked', result }
  const warnings = result.warnings
  if (warnings === undefined || warnings.length === 0) return null
  // 🔴 결과를 통째로 지닌다 — 통과한 회차에도 남는 **미판정 항목**을 같이 보이려는 것이다
  return { phase: 'warn', warnings, result }
}

/**
 * 확약해 보낼 **코드 집합**. 🔴 **목록 전부다 — 화면이 골라내지 않는다.**
 * 중복 제거·정렬은 `useStartBatch` 안의 `startWarnAck()` 이 한다(여기서 또 하지 않는다).
 */
export function ackCodes(gate: StartGate): string[] {
  return gate.phase === 'warn' ? gate.warnings.map((warning) => warning.code) : []
}

/**
 * 시작 실패 → 게이트. `null` 이면 **경고 확약 축이 아닌 실패**다(상태 충돌 · 권한 · 5xx …) —
 * 그건 기존 명령 실패 표시 자리에 글자로 나간다.
 *
 * 🔴 **서버가 정답을 같이 준다.** 세 갈래 전부 409 `BATCH_COMMAND_NOT_ALLOWED` 이고
 *    `context.reason` 이 가른다(`ctcells-core app/views/batches.py:679-709`):
 *    | `reason`                    | 서버가 실어 주는 정답                       |
 *    | --------------------------- | ------------------------------------------ |
 *    | `warn_ack_required`         | `context.warnings` — **항목 전체**(DTO 꼴)  |
 *    | `warn_ack_snapshot_missing` | `context.warnings` — 같음                   |
 *    | `warn_ack_mismatch`         | `context.actual` — **코드만**               |
 *
 * ⚠ `context.warnings` 는 `model_dump(exclude_none=True)` 를 지나 **빈 칸이 아예 빠진다** —
 *   `null` 로 채워 받는다(「서버가 안 준 칸」과 「없는 값」은 화면에서 같은 뜻이다).
 * ⚠ `warn_ack_mismatch` 는 코드밖에 안 온다. **지난 목록의 `detail` 을 얹지 않는다** —
 *   그 수치는 이미 낡았고, 낡은 수를 「지금 잰 값」인 척 보이는 것이 이 칸이 막으려는 일이다.
 */
export function startGateFromError(error: ApiError): StartGate | null {
  const reason = warnAckReasonOf(error)
  if (reason === undefined) return null
  const context = error.context ?? {}

  if (reason === 'warn_ack_mismatch') {
    const codes = asCodes(context['actual'])
    return codes === undefined ? null : { phase: 'warn', warnings: codes.map(bareWarning), reason }
  }

  const warnings = asWarnings(context['warnings'])
  return warnings === undefined || warnings.length === 0
    ? null
    : { phase: 'warn', warnings, reason }
}

/** 409 `BATCH_COMMAND_NOT_ALLOWED` 의 `context.reason` 이 확약 축인가 */
export function warnAckReasonOf(error: ApiError): WarnAckReason | undefined {
  if (error.status !== 409) return undefined
  const reason = (error.context ?? {})['reason']
  return reason === 'warn_ack_required' ||
    reason === 'warn_ack_snapshot_missing' ||
    reason === 'warn_ack_mismatch'
    ? reason
    : undefined
}

/**
 * 대화를 **다시 세우는 열쇠**. 목록이 갈리면 값이 갈려서 확약 체크가 저절로 풀린다.
 * 🔴 **한 번 확인한 체크가 새 목록으로 넘어가면 안 된다** — 「다른 것을 보고 확약한 것」이다.
 */
export function startGateKey(gate: StartGate): string {
  if (gate.phase === 'confirm') return 'confirm'
  if (gate.phase === 'blocked') return `blocked:${String(gate.result.blockers.length)}`
  return `warn:${gate.reason ?? 'preflight'}:${[...new Set(ackCodes(gate))].sort().join(',')}`
}

/** 코드밖에 못 받은 경고 한 건. 나머지 칸은 **서버가 안 준 것**이라 `null` 이다 */
function bareWarning(code: string): PreflightWarning {
  return { code, item: null, module_id: null, detail: null, action_required: null }
}

function asCodes(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const codes = raw.filter((value): value is string => typeof value === 'string')
  return codes.length === 0 ? undefined : codes
}

function asWarnings(raw: unknown): PreflightWarning[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const warnings: PreflightWarning[] = []
  for (const value of raw) {
    if (typeof value !== 'object' || value === null) continue
    const row = value as Record<string, unknown>
    const code = row['code']
    if (typeof code !== 'string') continue
    warnings.push({
      code,
      item: asText(row['item']),
      module_id: asText(row['module_id']),
      detail: asText(row['detail']),
      action_required: asText(row['action_required']),
    })
  }
  return warnings.length === 0 ? undefined : warnings
}

function asText(raw: unknown): string | null {
  return typeof raw === 'string' ? raw : null
}
